import React, { createContext, useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { createHook } from 'utils/utils';
import { translations } from 'i18n';
import { usePrediction } from './PredictionHook';
import { Device, useConnectedDevices } from './ConnectedDevicesHook';
import KeonWiFi from 'lib/devices/wrappers/KeonWiFi';
import { DeviceInfoTypes } from 'lib/serverModel';
import {
  getRegistrationTokenAndAccessToken,
  getWifiAccessTokenAndRefreshToken,
} from 'lib/WifiTokens';
import { useSubscription } from './SubscriptionHook';
import {
  extractDeviceConnectionKeyFromRegistrationToken,
  setWiFiConnectionKeyOnLocalStorage,
  getWifiConnectionKeyOnLocalStorage,
  clearWiFiConnectionKeyOnLocalStorage,
} from 'api/oauth-api';

const DEVICE_STATUS_INTERVAL_SEC = 60;

export interface CcWssDevicesContextInterface {
  wifiDevices: DeviceWiFi[];
  findDevice: (sid: string, serial_number: string, bleAddress?: string) => number;
  sendMessageToDevices: (eventName: string, args: any) => void;
  setupWIFIInfoAndProvisioning: (
    ssid: string,
    password: string,
    connectionKeyInput: string,
  ) => void;
  connectToWIFIViaConnectionKey: (connectionKeyInput: string) => void;
  setConnectingStatus: (status: 'idle' | 'connecting' | 'success' | 'failed') => void;
  connectingStatus: 'idle' | 'connecting' | 'success' | 'failed';
  deviceConnectionKey: string | null;
  batteryStatus: number | null;
  removeConnectionFromServer: () => void;
}

const CcWssDevicesContext = createContext<CcWssDevicesContextInterface | null>(null);
export const useCcWssDevices = () => createHook('useCcWssDevices', CcWssDevicesContext);

export type DeviceWiFi = Device & {
  sid: string;
  serialNumber: string;
  bleAddress: string;
};

type Props = {
  children: React.ReactNode;
};
export const WssConnectedDevicesContextProvider = (props: Props) => {
  const { connection } = usePrediction();
  const { tryToConnectToWiFiDevice, disconnect, devices, currentDevice } = useConnectedDevices();
  const { setAccessToken, registrationToken } = useSubscription();
  const [deviceConnectionKey, setDeviceConnectionKey] = useState<string | null>(
    getWifiConnectionKeyOnLocalStorage(),
  );
  const [wifiDevices, setWifiDevices] = useState<DeviceWiFi[]>([]);
  const wifiDevicesRef = useRef<DeviceWiFi[]>(wifiDevices);
  const [connectingStatus, setConnectingStatus] = useState<
    'idle' | 'connecting' | 'success' | 'failed'
  >('idle');
  const [batteryStatus, setBatteryStatus] = useState<number | null>(null);
  // Timer to detect lack of device response after initiating connection/provisioning
  const connectionResponseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Keep ref in sync to avoid stale reads inside subscriptions/callbacks
  useEffect(() => {
    wifiDevicesRef.current = wifiDevices;
  }, [wifiDevices]);

  const findDevice = (sid?: string, serialNumber?: string, bleAddress?: string): number => {
    const list = wifiDevicesRef.current;
    let index: number = -1;
    if (sid) {
      index = list.findIndex((dev: DeviceWiFi) => dev.sid === sid);
      if (index !== -1) return index;
    }
    if (serialNumber) {
      index = list.findIndex((dev: DeviceWiFi) => dev.serialNumber === serialNumber);
      if (index !== -1) return index;
    }
    if (bleAddress) {
      index = list.findIndex((dev: DeviceWiFi) => dev.bleAddress === bleAddress);
      if (index !== -1) return index;
    }
    return -1;
  };

  const sendMessageToDevices = (eventName: string, args: any) => {
    connection.sendMessage(eventName, args);
  };

  const clearConnectionResponseTimer = () => {
    if (connectionResponseTimerRef.current) {
      clearTimeout(connectionResponseTimerRef.current);
      connectionResponseTimerRef.current = null;
    }
  };

  const startConnectionResponseTimer = () => {
    clearConnectionResponseTimer();
    connectionResponseTimerRef.current = setTimeout(() => {
      setConnectingStatus('failed');
    }, 37000);
  };

  const updatingDevice = async (
    sid: string,
    serialNumber: string,
    payload: any,
    isTryingToConnect?: boolean,
  ) => {
    const deviceInfo = payload?.KEON2 ?? payload?.KEON;
    const idx = findDevice(sid, serialNumber, deviceInfo?.ble_address);

    if (idx >= 0 && !isTryingToConnect) {
      const existing = wifiDevicesRef.current[idx];
      const deviceWrapper = existing.wrapper as KeonWiFi;
      // Update wrapper first
      deviceWrapper?.refreshStatus?.(sid, deviceInfo);
      // Immutably update the state array so React re-renders
      setWifiDevices((prev) => {
        const next = [...prev];
        next[idx] = {
          ...(prev[idx] as DeviceWiFi),
          sid,
          serialNumber,
          bleAddress: deviceInfo.ble_address,
        } as DeviceWiFi;
        return next;
      });
    } else {
      // Derive device name from payload to avoid depending on currentDevice being defined.
      // payload.KEON2 signals a Keon2 device; payload.KEON signals a KEON WIFI device.
      const wifiDeviceName = payload?.KEON2 ? 'KEON2' : (currentDevice?.name ?? 'KEON WIFI');
      const wifiDevice: Device = {
        name: wifiDeviceName,
        company: 'Kiiroo',
        supportWifi: true,
      };
      const deviceWrapper = await tryToConnectToWiFiDevice(wifiDevice);

      deviceWrapper.refreshStatus?.(sid, deviceInfo);
      deviceWrapper.setServerChanels(sendMessageToDevices);

      const newDevice: DeviceWiFi = {
        sid: sid,
        serialNumber: serialNumber,
        bleAddress: deviceInfo.ble_address,
        wrapper: deviceWrapper as KeonWiFi,
      };
      setWifiDevices((prev) => [newDevice, ...prev]);
    }
  };

  const deviceDisconnecting = async (sid: string, serial_number: string, payload: any) => {
    const idx = findDevice(sid, serial_number);
    if (idx >= 0) {
      const deviceWrapper = wifiDevicesRef.current[idx].wrapper as KeonWiFi;
      // Remove by index using functional update
      setWifiDevices((prev) => prev.filter((_, index) => index !== idx));
      const index = devices.findIndex((dev: Device) => dev.name === deviceWrapper.device.name);
      if (index !== -1) {
        await disconnect(index);
      }
    }
  };

  useEffect(() => {
    if (!connection) {
      return;
    }

    wifiDevices.forEach((dev: DeviceWiFi) => {
      const wrapper = dev.wrapper as KeonWiFi;
      wrapper.setServerChanels(sendMessageToDevices);
    });

    const unsubscribeFromDeviceStatus = connection.subscribeDeviceStatus(
      async ({ sid, serial_number, info_type, payload }) => {
        switch (info_type) {
          case DeviceInfoTypes.STATUS: {
            // If we were waiting for a response, mark success; otherwise just update state
            const wasWaitingForResponse = !!connectionResponseTimerRef.current;
            if (wasWaitingForResponse) {
              await updatingDevice(sid, serial_number, payload, true);
              setConnectingStatus('success');
              clearConnectionResponseTimer();
            } else {
              await updatingDevice(sid, serial_number, payload);
            }
            const battery = (payload?.KEON2 ?? payload?.KEON)?.battery_status;
            if (battery != null) setBatteryStatus(battery);
            break;
          }
          case DeviceInfoTypes.DISCONNECTED:
            await deviceDisconnecting(sid, serial_number, payload);
            break;
          default:
            return null;
        }
      },
    );

    const getDeviceStatusInterval = setInterval(() => {
      connection.requestStatusOfDevices();
    }, DEVICE_STATUS_INTERVAL_SEC * 1000);

    return () => {
      unsubscribeFromDeviceStatus();
      clearInterval(getDeviceStatusInterval);
    };
  }, [connection]);

  // Validates and normalizes the device connection key. Returns normalized key or null on error.
  const validateAndNormalizeConnectionKey = (connectionKeyInput: string): string | null => {
    const normalizedKey = (connectionKeyInput || '').toUpperCase().trim();
    if (!normalizedKey) return null;
    const validKeyRegex = /^[0-9AC-HJ-NPR-Y]{8}$/; // excludes B, I, O, Q, Z
    if (!validKeyRegex.test(normalizedKey)) {
      const msg = translations('DevicesBox.InvalidConnectionKeyFormat');
      toast.error(msg);
      return null;
    }
    return normalizedKey;
  };

  const setupWIFIInfoAndProvisioning = async (
    ssid: string,
    password: string,
    connectionKeyInput: string,
  ) => {
    const normalizedKey = validateAndNormalizeConnectionKey(connectionKeyInput);
    if (connectionKeyInput && !normalizedKey) return;
    setConnectingStatus('connecting');

    try {
      const { registrationToken: newRegistrationToken, accessToken } =
        await getRegistrationTokenAndAccessToken(normalizedKey, true);
      setWiFiConnectionKeyOnLocalStorage(
        extractDeviceConnectionKeyFromRegistrationToken(newRegistrationToken),
      );

      const extractConnectionKey =
        extractDeviceConnectionKeyFromRegistrationToken(newRegistrationToken);
      setDeviceConnectionKey(extractConnectionKey);
      setAccessToken(accessToken);
      startConnectionResponseTimer();

      if (ssid && password) {
        await currentDevice.wrapper?.provisioning(ssid, password, newRegistrationToken);
      }
    } catch (error) {
      clearConnectionResponseTimer();
      setConnectingStatus('failed');
    }
  };

  const removeConnectionFromServer = async () => {
    try {
      setConnectingStatus('connecting');
      const { accessToken } = await getRegistrationTokenAndAccessToken('', true);
      setAccessToken(accessToken);

      currentDevice.wrapper?.resetDeviceStatus();
      setDeviceConnectionKey(null);
      clearWiFiConnectionKeyOnLocalStorage();
      setConnectingStatus('idle');
    } catch (error) {
      console.error(error);
      setConnectingStatus('idle');
    }
  };

  const connectToWIFIViaConnectionKey = async (connectionKeyInput: string) => {
    const normalizedKey = validateAndNormalizeConnectionKey(connectionKeyInput);
    if (connectionKeyInput && !normalizedKey) {
      return;
    }
    const oldConnectionKey = getWifiConnectionKeyOnLocalStorage();
    if (oldConnectionKey && oldConnectionKey === normalizedKey) {
      try {
        setConnectingStatus('connecting');
        const { accessToken } = await getWifiAccessTokenAndRefreshToken(normalizedKey);
        setAccessToken(accessToken);
        startConnectionResponseTimer();
      } catch (error) {
        clearConnectionResponseTimer();
        setConnectingStatus('failed');
      }
    } else {
      setupWIFIInfoAndProvisioning('', '', connectionKeyInput);
    }
  };

  return (
    <CcWssDevicesContext.Provider
      value={{
        wifiDevices,
        findDevice,
        sendMessageToDevices,
        setupWIFIInfoAndProvisioning,
        connectToWIFIViaConnectionKey,
        setConnectingStatus,
        connectingStatus,
        deviceConnectionKey,
        batteryStatus,
        removeConnectionFromServer,
      }}
    >
      {props.children}
    </CcWssDevicesContext.Provider>
  );
};
